import { useNavigate } from 'react-router-dom';
import { useAlertsStore, ALERT_CATEGORY, ALERT_SEVERITY, type Alert } from '../../stores/alertsStore';
import './AttentionRows.css';

/**
 * Problems with no card of their own.
 *
 * Every other alert the app raises has a matching dashboard card — an
 * admission has Admitted Residents, an allergy change has Allergy Changes. Two
 * do not, and they were reaching the notification bell and nowhere else:
 *
 *   ehrMessage  a message that arrived and did not land
 *   pdpmGap     documentation missing against a PDPM assessment
 *
 * Both are aggregate facts about the facility rather than lists of residents,
 * so a card full of rows is the wrong shape — one line each, stating the
 * count and where to go, is the right one.
 *
 * No new fetching: `useAlertPolling` already populates the alerts store for
 * the bell, and this reads the same store. The dashboard and the bell cannot
 * disagree, because they are the same data.
 */

/**
 * Categories that have a card. Anything else is rendered here.
 *
 * Written as an exclusion list rather than an inclusion list on purpose: a new
 * alert category added later shows up on the dashboard by default instead of
 * silently reaching only the bell, which is the bug this component fixes.
 */
const HAS_A_CARD: ReadonlySet<string> = new Set([
  ALERT_CATEGORY.admit,
  ALERT_CATEGORY.discharge,
  ALERT_CATEGORY.allergyChange,
  ALERT_CATEGORY.dietOrderChange,
  ALERT_CATEGORY.trayLineWarning,
  ALERT_CATEGORY.unitRoomBedChange,
  ALERT_CATEGORY.mealPatternNote,
  ALERT_CATEGORY.selectiveMenu,
  ALERT_CATEGORY.bedsideDevice,
]);

const SEVERITY_RANK: Record<string, number> = {
  [ALERT_SEVERITY.critical]: 0,
  [ALERT_SEVERITY.warning]: 1,
  [ALERT_SEVERITY.info]: 2,
};

export function selectAttentionAlerts(alerts: Alert[]): Alert[] {
  return alerts
    .filter((a) => !HAS_A_CARD.has(a.category))
    .sort((a, b) => {
      const s = (SEVERITY_RANK[a.severity] ?? 3) - (SEVERITY_RANK[b.severity] ?? 3);
      if (s !== 0) return s;
      // Oldest first within a severity: for EHR the age IS the problem, so
      // the longest-stuck backlog should be the one you see first.
      return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
    });
}

export default function AttentionRows() {
  const navigate = useNavigate();
  const alerts = useAlertsStore((s) => s.alerts);
  const rows = selectAttentionAlerts(alerts);

  if (rows.length === 0) return null;

  return (
    <div className="attn-rows">
      {rows.map((a) => (
        <button
          key={a.id}
          type="button"
          className={`attn-row attn-row--${a.severity}`}
          onClick={() => a.link && navigate(a.link)}
          disabled={!a.link}
        >
          <i
            className={`bi ${a.severity === ALERT_SEVERITY.critical ? 'bi-exclamation-octagon-fill' : 'bi-exclamation-triangle-fill'} attn-row-icon`}
            aria-hidden="true"
          />
          <span className="attn-row-title">{a.title}</span>
          <span className="attn-row-message">{a.message}</span>
          {a.link && <i className="bi bi-arrow-right attn-row-go" aria-hidden="true" />}
        </button>
      ))}
    </div>
  );
}
